import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api';

export default function Checklist({ type, entityId, entityName, jobId, onClose }) {
  const { user, isDirector } = useAuth();
  const { toast } = useToast();
  const [template, setTemplate]   = useState(null);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

    useEffect(() => {
    if (type && entityId) loadTemplate();
  }, [type, entityId, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadTemplate() {
    setLoading(true);
    api.get(`/api/v1/checklists/${type}/${entityId}`)
      .then(r => {
        setTemplate(r.data);
        const templateItems = r.data.checklist_template_items || [];
        // Load last completion to pre-fill checked state
        return api.get(`/api/v1/checklists/${r.data.id}/last`)
          .then(lastRes => {
            const lastCompletion = lastRes.data;
            if (lastCompletion && lastCompletion.checklist_completion_items) {
              const completedMap = {};
              lastCompletion.checklist_completion_items.forEach(ci => {
                completedMap[ci.template_item_id] = ci;
              });
              setItems(templateItems.map(item => ({
                ...item,
                checked:    completedMap[item.id]?.checked || false,
                checked_at: completedMap[item.id]?.checked_at || null,
                checked_by: completedMap[item.id]?.note?.replace('Checked by ', '') || null,
                note:       '',
              })));
            } else {
              setItems(templateItems.map(item => ({
                ...item, checked: false, checked_at: null, note: '',
              })));
            }
            setLoading(false);
          })
          .catch(() => {
            setItems(templateItems.map(item => ({
              ...item, checked: false, checked_at: null, note: '',
            })));
            setLoading(false);
          });
      })
      .catch(() => setLoading(false));
  }

  function toggleItem(id) {
    setItems(prev => prev.map(item =>
      item.id === id
        ? { ...item, checked: !item.checked, checked_at: !item.checked ? new Date().toISOString() : null, checked_by: !item.checked ? user?.name : null }
        : item
    ));
    setSaved(false);
  }

  function updateNote(id, note) {
    setItems(prev => prev.map(item => item.id === id ? { ...item, note } : item));
    setSaved(false);
  }

function saveCompletion() {
    setSaving(true);
    api.post('/api/v1/checklists/complete', {
      template_id:  template.id,
      job_id:       jobId || null,
      completed_by: user?.name,
      items: items.map(item => ({
        template_item_id: item.id,
        checked:          item.checked,
        note:             item.note || (item.checked_by ? `Checked by ${item.checked_by}` : ''),
      })),
    }).then(() => {
      setSaving(false);
      setSaved(true);
    }).catch(err => {
      console.error('Checklist save failed:', err);
      setSaving(false);
      toast.error(err.response?.data?.error || 'Failed to save checklist — please try again');
    });
  }

  function saveTemplate() {
    setSaving(true);
    api.put(`/api/v1/checklists/${template.id}/items`, {
      items: items.map((item, i) => ({
        text:     item.text,
        required: item.required,
        order_index: i,
      })),
    }).then(r => {
      setTemplate(r.data);
      setItems((r.data.checklist_template_items || []).map(item => ({
        ...item, checked: false, checked_at: null, note: '',
      })));
      setEditing(false);
      setSaving(false);
    }).catch(() => setSaving(false));
  }

  function addItem() {
    setItems(prev => [...prev, { id: `new-${Date.now()}`, text: '', required: true, checked: false, note: '' }]);
  }

  function removeItem(id) {
    setItems(prev => prev.filter(item => item.id !== id));
  }

  function updateItemText(id, text) {
    setItems(prev => prev.map(item => item.id === id ? { ...item, text } : item));
  }

  const checkedCount = items.filter(i => i.checked).length;
  const allChecked   = items.length > 0 && checkedCount === items.length;

  const typeColors = {
    machine: { bg: '#1e3a5f', border: '#1d4ed8', text: '#93c5fd' },
    mould:   { bg: '#14532d', border: '#166534', text: '#86efac' },
    fai:     { bg: '#3b1f5e', border: '#7c3aed', text: '#c4b5fd' },
  };
  const colors = typeColors[type] || typeColors.machine;

  if (loading) return <div style={cs.center}>Loading checklist...</div>;

  return (
    <div style={cs.container}>
      {/* Header */}
      <div style={{ ...cs.header, background: colors.bg, border: `1px solid ${colors.border}` }}>
        <div>
          <div style={{ ...cs.headerTitle, color: colors.text }}>
            {type === 'machine' ? '⚙️ Machine Checklist' : type === 'mould' ? '🔲 Mould Checklist' : '🔍 FAI Checklist'}
          </div>
          <div style={cs.headerSub}>{entityName}</div>
        </div>
        <div style={cs.headerRight}>
          <div style={{ ...cs.progress, color: allChecked ? '#22c55e' : colors.text }}>
            {checkedCount}/{items.length}
          </div>
          {isDirector() && !editing && (
            <button style={cs.editBtn} onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div style={cs.progressBar}>
        <div style={{ ...cs.progressFill, width: `${items.length > 0 ? (checkedCount / items.length) * 100 : 0}%`, background: allChecked ? '#22c55e' : colors.border }} />
      </div>

      {/* Items */}
      <div style={cs.itemList}>
        {items.map((item, idx) => (
          <div key={item.id} style={{ ...cs.item, background: item.checked ? '#0f2d1f' : '#1e2329', borderColor: item.checked ? '#166534' : '#2a3040' }}>
            {editing ? (
              <div style={cs.editRow}>
                <input
                  style={cs.editInput}
                  value={item.text}
                  onChange={e => updateItemText(item.id, e.target.value)}
                  placeholder="Checklist item..."
                />
                <button style={cs.removeBtn} onClick={() => removeItem(item.id)}>×</button>
              </div>
            ) : (
              <>
                <div style={cs.itemMain} onClick={() => toggleItem(item.id)}>
                  <div style={{ ...cs.checkbox, background: item.checked ? '#22c55e' : 'transparent', borderColor: item.checked ? '#22c55e' : '#4b5563' }}>
                    {item.checked && <span style={cs.checkmark}>✓</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ ...cs.itemText, color: item.checked ? '#86efac' : '#f0f2f5', textDecoration: item.checked ? 'line-through' : 'none' }}>
                      {item.text}
                      {item.required && <span style={cs.required}>*</span>}
                    </div>
                    {item.checked && item.checked_by && (
                      <div style={cs.checkedBy}>✓ {item.checked_by} · {item.checked_at ? new Date(item.checked_at).toLocaleTimeString() : ''}</div>
                    )}
                  </div>
                </div>
                {item.checked && (
                  <input
                    style={cs.noteInput}
                    value={item.note}
                    onChange={e => updateNote(item.id, e.target.value)}
                    placeholder="Add note (optional)..."
                    onClick={e => e.stopPropagation()}
                  />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Edit mode — add item */}
      {editing && (
        <button style={cs.addItemBtn} onClick={addItem}>+ Add item</button>
      )}

      {/* Footer buttons */}
      <div style={cs.footer}>
        {editing ? (
          <>
            <button style={cs.cancelBtn} onClick={() => { setEditing(false); loadTemplate(); }}>Cancel</button>
            <button style={{ ...cs.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={saveTemplate}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </>
        ) : (
          <>
            <div style={cs.statusMsg}>
              {saved ? <span style={{ color: '#22c55e' }}>✓ Saved</span> :
               allChecked ? <span style={{ color: '#22c55e' }}>All items checked</span> :
               <span style={{ color: '#6b7280' }}>{items.length - checkedCount} remaining</span>}
            </div>
            <button style={{ ...cs.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving || items.length === 0} onClick={saveCompletion}>
              {saving ? 'Saving...' : 'Save Checklist'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const cs = {
  container:    { fontFamily: 'system-ui, sans-serif', color: '#f0f2f5' },
  center:       { padding: '32px', textAlign: 'center', color: '#6b7280' },
  header:       { borderRadius: '10px', padding: '14px 16px', marginBottom: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerTitle:  { fontSize: '14px', fontWeight: '700', marginBottom: '2px' },
  headerSub:    { fontSize: '11px', color: '#9ca3af' },
  headerRight:  { display: 'flex', alignItems: 'center', gap: '10px' },
  progress:     { fontSize: '20px', fontWeight: '700', fontFamily: 'monospace' },
  editBtn:      { background: 'none', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' },
  progressBar:  { height: '3px', background: '#2a3040', marginBottom: '12px' },
  progressFill: { height: '100%', borderRadius: '2px', transition: 'width 0.3s ease' },
  itemList:     { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  item:         { border: '1px solid', borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' },
  itemMain:     { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  checkbox:     { width: '20px', height: '20px', borderRadius: '4px', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
  checkmark:    { fontSize: '13px', fontWeight: '700', color: '#fff' },
  itemText:     { fontSize: '13px', lineHeight: '1.4' },
  required:     { color: '#ef4444', marginLeft: '4px', fontSize: '10px' },
  checkedBy:    { fontSize: '10px', color: '#6b7280', marginTop: '4px' },
  noteInput:    { width: '100%', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#9ca3af', padding: '6px 8px', fontSize: '11px', marginTop: '8px', boxSizing: 'border-box' },
  editRow:      { display: 'flex', gap: '8px', alignItems: 'center' },
  editInput:    { flex: 1, background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px' },
  removeBtn:    { background: 'none', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer', padding: '0 4px' },
  addItemBtn:   { width: '100%', background: 'none', border: '1px dashed #2a3040', color: '#6b7280', borderRadius: '8px', padding: '8px', fontSize: '12px', cursor: 'pointer', marginBottom: '12px' },
  footer:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #2a3040' },
  statusMsg:    { fontSize: '12px' },
  cancelBtn:    { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer' },
  saveBtn:      { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
};